// Wave AW.3-React (2026-05-16) — PROMISE (RFQ) 목록 화면
// API: GET /buyer-app/rfq — open RFQ 목록 (매너 ≥ 30°)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized } from './buyerApi';
import AuctionBuyerLayout from './AuctionBuyerLayout';

function formatKrw(n) { if (n == null) return '-'; return Number(n).toLocaleString() + '원'; }
function formatMileage(km) { if (km == null) return ''; return Number(km).toLocaleString() + ' km'; }

// RFQ 사진 URL — photos 는 array<string> 또는 object<slot, string>
function getThumbUrl(rfq) {
  var photos = rfq.photos;
  if (!photos) return null;
  var path = null;
  if (Array.isArray(photos)) {
    path = photos[0];
  } else if (typeof photos === 'object') {
    // legacy named slots: exterior / interior / ... 우선
    path = photos.exterior || photos.front || photos[Object.keys(photos)[0]];
  }
  if (!path) return null;
  // path 가 'uploads/...' 또는 '/uploads/...' 또는 absolute URL
  if (/^https?:/.test(path)) return path;
  var origin = API.replace(/\/api$/, '');
  return origin + (path.charAt(0) === '/' ? path : '/' + path);
}

function PromiseCard(props) {
  var rfq = props.rfq;
  var thumb = getThumbUrl(rfq);
  var hasMyQuote = !!rfq.ownQuote;
  var myPrice = hasMyQuote ? rfq.ownQuote.priceKrw : 0;

  return (
    <div onClick={props.onClick}
      style={{
        background: 'white', borderRadius: 12,
        border: '1px solid #e2e8f0',
        cursor: 'pointer', overflow: 'hidden',
        transition: 'all 0.15s',
      }}
      onMouseEnter={function(e){e.currentTarget.style.boxShadow='0 8px 20px rgba(15,23,42,0.08)';e.currentTarget.style.transform='translateY(-2px)';}}
      onMouseLeave={function(e){e.currentTarget.style.boxShadow='none';e.currentTarget.style.transform='none';}}>
      <div style={{ position: 'relative', width: '100%', aspectRatio: '4/3', background: '#f1f5f9' }}>
        {thumb ? (
          <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13 }}>
            사진 없음
          </div>
        )}
        {hasMyQuote && (
          <div style={{ position: 'absolute', top: 10, left: 10, background: '#3b82f6', color: 'white', padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
            내 시세 {formatKrw(myPrice)}
          </div>
        )}
        {rfq.ownerConsented && (
          <div style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(5,150,105,0.92)', color: 'white', padding: '4px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700 }}>
            차주 동의
          </div>
        )}
      </div>
      <div style={{ padding: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {rfq.year ? rfq.year + ' ' : ''}{rfq.model || '차량'}
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8', display: 'flex', gap: 8, marginBottom: 8 }}>
          {rfq.carNo && <span>{rfq.carNo}</span>}
          {formatMileage(rfq.mileage) && <span>·</span>}
          {formatMileage(rfq.mileage) && <span>{formatMileage(rfq.mileage)}</span>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: '1px solid #f1f5f9', fontSize: 11, color: '#94a3b8' }}>
          <span>시세 {rfq.quoteCount || 0}개</span>
          {rfq.sellerTier && rfq.sellerTier.label && (
            <span style={{ color: rfq.sellerTier.color || '#94a3b8', fontWeight: 700 }}>{rfq.sellerTier.short || rfq.sellerTier.label}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function AuctionBuyerPromiseList() {
  var navigate = useNavigate();
  var [items, setItems] = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');

  var load = useCallback(function() {
    setLoading(true); setError('');
    authBuyerFetch(API + '/buyer-app/rfq?limit=50&offset=0', {})
      .then(function(r){
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json();
      })
      .then(function(d){
        if (d && d.success && Array.isArray(d.data)) {
          setItems(d.data);
        } else if (d && d.code === 'BUYER_TRUST_TOO_LOW') {
          setError('매너온도가 낮아 시세 조회/제출이 제한됩니다 (필요 30°)');
        } else {
          setError((d && d.error) || '목록 로드 실패');
        }
        setLoading(false);
      })
      .catch(function(e){
        if (e && e.message === 'unauthorized') return;
        setError('네트워크 오류'); setLoading(false);
      });
  }, [navigate]);

  useEffect(function(){ load(); }, [load]);

  return (
    <AuctionBuyerLayout activeKey="promise">
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 32px 80px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>PROMISE 사전조회</h2>
        </div>
        <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
          매입 전 차량의 사진을 보고 시세를 미리 제시해보세요. 출품자가 시세를 선택하면 거래가 진행됩니다.
        </p>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: 14, marginBottom: 20, color: '#991b1b', fontSize: 14 }}>
            ⚠ {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>로딩 중...</div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
            <div style={{ fontSize: 40, opacity: 0.4, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>진행중 PROMISE 가 없습니다</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>새 매물이 등록되면 표시됩니다</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {items.map(function(rfq){
              return (
                <PromiseCard key={rfq.id} rfq={rfq}
                  onClick={function(){ navigate('/buyer/promise/' + rfq.id); }}
                />
              );
            })}
          </div>
        )}
      </div>
    </AuctionBuyerLayout>
  );
}

export default AuctionBuyerPromiseList;
